import React, { useState, useEffect, use } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AsYouType, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

// utils
import showToast from '../../utils/toastNotifications';

// hooks
import { useThunk } from '../../hooks/useThunk';

// thunks
import { getTenantPaymentDetails, getCheckoutSession, getPaymentConfirmation } from '../../store';

// components
import { TableSection } from "../../components/Tenant/TenantTable";
import ConfirmationPaymentModal from "../../components/Model/ConfirmationPaymentModal";
import PaymentFailedModal from "../../components/Model/PaymentFailedModal";


const TenantRentPayment = () => {
  const { unitCode } = useParams();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const navigate = useNavigate();
  const [paymentTenantDetails, setPaymentTenantDetails] = useState(null);
  const [amount, setAmount] = useState();
  const [mobileNo, setMobileNo] = useState('');
  const [mobileNoError, setMobileNoError] = useState("");
  const [paymentConfirmationData, setPaymentConfirmationData] = useState(null);
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const [amountError, setAmountError] = useState("");

  const [getPaymentDetails, isLoading, error] = useThunk(getTenantPaymentDetails);
  const [checkoutData, setCheckoutData] = useState(null);
  const [isGetCheckoutSession, isCheckoutLoading, checkoutError] = useThunk(getCheckoutSession);
  const [getPaymentConfirmationDetails, isConfirmationLoading, confirmationError] = useThunk(getPaymentConfirmation);
  
  // Fetch payment details when component mounts
  useEffect(() => {
    if (unitCode) {
      handleGetPaymentDetails(unitCode);
    }
  }, [unitCode]);

  // Check for payment success in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get('status');
    const txRef = urlParams.get('transaction_id');

    if (paymentStatus === 'successful' && txRef) {
      // Fetch payment confirmation details
      console.log("Payment successful:", txRef);
      handleGetPaymentConfirmation(txRef);
      
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location.search]);

  // useEffect(() => {
  //   handleGetPaymentConfirmation();
  // }, []);

  const handleGetPaymentConfirmation = async (transactionId) => {
    const data = await getPaymentConfirmationDetails(transactionId);
    
    if (data && data.status === 200) {
      setPaymentConfirmationData(data.data);
      setIsModalOpen(true);
      showToast("success", "Payment completed successfully!");
    }
  };

  const handleGetPaymentDetails = async (code) => {
    if (!code) {
      showToast("error", "Please enter your unique code to proceed.");
      return;
    }
    const data = await getPaymentDetails({ unitcode:unitCode });
    if (!data) {
      showToast("error", "Failed to fetch payment details. Please try again.");
      return;
    }
    if (data.success === false) {
      showToast("error", data.error.message);
      return;
    }
    if (data.success === true) {
      // Payment details fetched successfully
      setPaymentTenantDetails(data.response.data);
      setCheckoutData(data.response.data.checkoutSession);
      showToast("success", "Payment details loaded successfully.");
    }
  };

  const formatAmount = (amount) => {
    // Format number with commas (e.g., 500000 -> "500,000")
    if (amount === null || amount === undefined || isNaN(amount)) return '';
    return Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPaidUpToDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const paymentStatusData = paymentTenantDetails?.paymentStatus
    ? paymentTenantDetails.paymentStatus.map(item => ({
        key: item._id,
        month: `${item.month} ${item.year}`,
        amountDue: formatAmount(item.amount),
      }))
    : [];
  
  const tenantData = [
    {
      key: '1',
      tenantName: 'Loading...',
      propertyName: 'Loading...',
      unitIdentifier: 'Loading...',
      landlordName: 'Loading...',
    },
  ];

  const tableColumns = [
    {
      title: 'Tenant Name',
      dataIndex: 'tenantName',
      key: 'tenantName',
      className: 'bg-indigo-100 text-blue-700 font-medium',
    },
    {
      title: 'Property Name',
      dataIndex: 'propertyName',
      key: 'propertyName',
      className: 'bg-indigo-100 text-blue-700 font-medium',
    },
    {
      title: 'Unit Identifier',
      dataIndex: 'unitIdentifier',
      key: 'unitIdentifier',
      className: 'bg-indigo-100 text-blue-700 font-medium',
    },
    {
      title: 'Landlord Name',
      dataIndex: 'landlordName',
      key: 'landlordName',
      className: 'bg-indigo-100 text-blue-700 font-medium',
    },
  ];

  const tableColumns2 = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      className: 'bg-indigo-100 text-blue-700 font-medium',
    },
    {
      title: 'Amount Due (UGX)',
      dataIndex: 'amountDue',
      key: 'amountDue',
      className: 'bg-indigo-100 text-blue-700 font-medium',
    },
  ];

  const tenantData2 = [
    {
      key: '1',
      month: 'Loading...',
      amountDue: 'Loading...',
    },
    {
      key: '2',
      month: 'Loading...',
      amountDue: 'Loading...',
    },
    {
      key: '3',
      month: 'Loading...',
      amountDue: 'Loading...',
    },
  ];

  const handlePayNow = async () => {
    // Validate payment amount
    if (!amount || parseFloat(amount) <= 0) {
      showToast("error", "Please enter a valid payment amount.");
      return;
    }

    // Validate minimum payment amount
    if (parseFloat(amount) < 3000) {
      showToast("error", "Payment amount must be at least UGX 3000.");
      return;
    }

    // Validate mobile number using the validation function
    const phoneError = validateInternationalPhoneNumber(mobileNo);
    if (phoneError) {
      setMobileNoError(phoneError);
      showToast("error", phoneError);
      return;
    }

    // Validate that payment details are loaded
    if (!checkoutData) {
      showToast("error", "Payment details not loaded. Please refresh the page.");
      return;
    }

    // Prepare checkout session data
    const checkoutPayload = {
      unitId: checkoutData.unitId,
      tenantId: checkoutData.tenantId,
      propertyId: checkoutData.propertyId,
      landlordId: checkoutData.landlordId,
      amount: parseFloat(amount),
      msisdn: mobileNo,
      isSecurityDeposit: false,
      unitCode: unitCode,
    };

    // Get checkout session
    const response = await isGetCheckoutSession(checkoutPayload);

    if (response && response.success === true) {
      // Show confirmation modal instead of redirecting
      const mockPaymentData = {
        summary: {
          tenantName: paymentTenantDetails?.tenantData?.tenantName,
          propertyName: paymentTenantDetails?.tenantData?.propertyName,
          unitIdentifier: paymentTenantDetails?.tenantData?.unitIdentifier,
          landlordName: paymentTenantDetails?.tenantData?.landlordName,
          totalAmount: parseFloat(amount),
          paymentDate: new Date().toISOString(),
          rentCoveredTo: new Date(response.response?.data?.data?.rentCoveredUptoDate).toISOString()
        },
        transactionId: response.response?.data?.data?.tx_ref || 'TXN' + Date.now()
      };
      
      setPaymentConfirmationData(mockPaymentData);
      setIsModalOpen(true);
      showToast("success", "Payment initiated successfully!");
    } else {
      // Show payment failed modal for any failure
      setIsFailedModalOpen(true);
      showToast("error", response?.error?.message || "Payment failed. Please try again.");
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    setIsModalOpen(false);
    navigate('/'); // Navigate to home page
  };

  const handleFailedModalClose = () => {
    setIsFailedModalOpen(false);
  };

  const handleFailedModalRetry = () => {
    setIsFailedModalOpen(false);
    // User can retry payment by clicking Pay Now again
  };

  const formatUGX = (value) => {
    if (!value) return '';
    const num = parseFloat(value.replace(/,/g, ''));
    if (isNaN(num)) return '';
    return 'UGX ' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatInternationalPhoneNumber = (input) => {
    let cleanInput = input.replace(/[^\d+]/g, '');
    if (cleanInput && !cleanInput.startsWith('+')) {
      cleanInput = '+' + cleanInput;
    }
    const formatter = new AsYouType();
    return formatter.input(cleanInput);
  };

  const validateInternationalPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim().length === 0) {
      return "Phone number is required";
    }
    
    if (!isValidPhoneNumber(phoneNumber)) {
      return "Please enter a valid phone number";
    }
    
    const parsed = parsePhoneNumberFromString(phoneNumber);
    
    if (!parsed) {
      return "Please enter a valid phone number with country code";
    }
    
    if (!parsed.isValid()) {
      return "Please enter a valid phone number";
    }
    
    return null;
  };

  const validateAmount = (value) => {
    if (!value || parseFloat(value) <= 0) {
      return "Please enter a valid payment amount.";
    }
    if (parseFloat(value) < 3000) {
      return "Payment amount must be at least UGX 3000.";
    }
    return "";
  };

  return (
    <div className="flex flex-col mt-[-30px] items-center bg-[#F6FCFF] px-5 lg:px-20 pb-14">
      <div className="max-w-[1200px] w-full p-[30px] h-auto rounded-[20px] bg-white shadow-[0px_4px_4px_0px_#00000017]">
        <h1 className="w-[229px] h-[37px] mt-[0px] font-alexandria font-semibold text-3xl leading-[37px] tracking-normal text-[#242527]">Rent Payment</h1>
        <p className="max-w-[538px] h-[26px] mt-[7px] font-poppins font-medium text-[12px] md:text-[17px] leading-[130%] tracking-normal text-start text-[#494949]">
          Review your rent details and complete your payment securely.
        </p>
        
        {/* Tenant & Property Information Section */}
        <TableSection 
          header="Tenant & Property Information"
          underlineWidth="308px"
          tableData={paymentTenantDetails?.tenantData ? [paymentTenantDetails.tenantData] : tenantData}
          tableColumns={tableColumns}
          centerContent={false}
          tableWidth="1100px"
        />
        
        {/* Payment Status Summary Section */}
        <TableSection 
          header="Payment Status Summary"
          underlineWidth="271px"
          tableData={paymentStatusData ? paymentStatusData : []}
          tableColumns={tableColumns2}
          centerContent={true}
          tableWidth="860px"
        >
            <div className="flex justify-center items-center md:ml-[6px]" style={{ width: "860px" }}>
              <div className="bg-[#4772F11A] text-[#4772F1ED] font-poppins font-medium p-[7px] text-[11px] leading-[17px] text-center rounded-[3px] w-auto h-auto flex items-center justify-center">
                {paymentTenantDetails?.paymentStatus?.length > 0 ? `Your rent is paid up to ${formatPaidUpToDate(paymentTenantDetails?.rentPaidUpTo)}`
                 : 'No dues remaining'}
              </div>
            </div>
        </TableSection>
        
        {/* Security Deposit Section */}
        { !paymentTenantDetails?.securityDeposit?.isPaid ? (<>
          <h1 className="font-poppins text-[#242527] font-semibold text-[20px] leading-none tracking-normal mt-[32px]">
            Security Deposit
          </h1>
          <div 
            className="absolute mt-1" 
            style={{ 
              background: 'linear-gradient(90deg, #60BBEE -2.22%, #2387C0 67.22%)',
              width: '167px',
              height: '2px'
            }}
          ></div>
          <div className="max-w-[1140px] w-full max-h-[135px] h-full mt-[22px] sm:mt-[24px] rounded-[10px] bg-[#84B2CD21] p-[20px] flex-row justify-between">
            <div className="max-w-[1100px] w-full max-h-[54px] h-full rounded-[8px] flex items-center px-6 justify-between"
              style={{ 
                background: 'linear-gradient(11.25deg, rgba(135, 63, 226, 0.1) 9.48%, rgba(154, 197, 255, 0.23) 92.9%)'
              }}>
              <h1 className="font-inter py-[15px] font-bold text-[10px] md:text-[16px] leading-[22px] tracking-[0%] text-[#4772F1] my-auto">
                Amount
              </h1>
              <h1 className="font-inter py-[15px] font-bold text-[10px] md:text-[20px] leading-[22px] tracking-[0%] text-[#7B7A7A] my-auto underline decoration-solid decoration-[#7B7A7A]">
                UGX {formatAmount(paymentTenantDetails?.securityDeposit?.remainingAmount)}
              </h1>
            </div>
            <div className="max-w-[161px] w-full mt-[10px] h-[31px] rounded-[3px] p-[7px] gap-[10px] bg-[#4772F11A] flex items-center justify-center">
              <span className="font-poppins font-medium text-[11px] leading-[17px] tracking-[0%] text-center text-[#4772F1ED]">
                This is a one-time deposit
              </span>
            </div>
          </div>
        </>) : null}

        {/* Payment Amount */}
        <h1 className="font-poppins text-[#242527] font-semibold text-[20px] leading-none tracking-normal mt-[32px]">
          Payment Amount
        </h1>
        <div 
          className="absolute mt-1" 
          style={{ 
            background: 'linear-gradient(90deg, #60BBEE -2.22%, #2387C0 67.22%)',
            width: '179px',
            height: '2px'
          }}
        ></div>
        <div className="max-w-[1140px] w-full max-h-[300px] mt-[22px] sm:mt-[24px] rounded-[10px] bg-[#84B2CD21] p-[20px] flex flex-col">
          <h1 className="font-inter font-medium text-[16px] leading-[22px] tracking-[0%] text-[#707070]">
            Enter Payment Amount
          </h1>
          <input 
            type="text"
            value={isAmountFocused ? amount : (amount ? formatUGX(amount) : '')}
            onFocus={() => setIsAmountFocused(true)}
            onBlur={() => {
              setIsAmountFocused(false);
              // Add .00 if amount doesn't have decimal places
              if (amount && !amount.includes('.')) {
                setAmount(amount + '.00');
              }
              // Validate amount on blur
              const error = validateAmount(amount);
              setAmountError(error);
            }}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9.]/g, '');
              const parts = raw.split('.');
              if (parts.length > 2) {
                return;
              }
              setAmount(raw);
              // Clear error when user starts typing
              if (amountError) {
                setAmountError("");
              }
            }}
            className={`max-w-[1100px] w-full h-[52px] mt-[5px] rounded-[8px] bg-white px-4 py-0 leading-[27px] shadow-[inset_0px_1px_3px_0px_#79797940] focus:outline-none font-inter text-[16px] flex items-center ${amountError ? 'border border-red-500' : ''}`}
            style={{ lineHeight: '27px', paddingTop: '12.5px', paddingBottom: '12.5px' }}
            placeholder="UGX 0.00"
          />
          {amountError && (
            <span className="text-red-500 text-[13px] font-inter block">{amountError}</span>
          )}
          
          <h1 className="font-inter font-medium text-[16px] leading-[22px] tracking-[0%] text-[#707070] mt-[15px]">
            Mobile Number
          </h1>
          <input 
            type="tel"
            value={mobileNo}
            onChange={(e) => {
              const masked = formatInternationalPhoneNumber(e.target.value);
              setMobileNo(masked);
              setMobileNoError(validateInternationalPhoneNumber(masked));
            }}
            onBlur={() => setMobileNoError(validateInternationalPhoneNumber(mobileNo))}
            className={`max-w-[1100px] w-full h-[52px] mt-[5px] rounded-[8px] bg-white px-4 py-0 leading-[27px] shadow-[inset_0px_1px_3px_0px_#79797940] focus:outline-none font-inter text-[16px] flex items-center ${mobileNoError ? 'border border-red-500' : ''}`}
            style={{ lineHeight: '27px', paddingTop: '12.5px', paddingBottom: '12.5px' }}
            placeholder="Enter your mobile number"
          />
          {mobileNoError && (
            <span className="text-red-500 text-[13px] font-inter block">{mobileNoError}</span>
          )}
          
          <div className="flex justify-start mt-[10px]">
            <div className="w-auto h-auto rounded-[3px] p-[7px] gap-[10px] bg-[#4772F11A] flex items-center justify-center">
              <span className="font-poppins font-medium text-[11px] leading-[17px] tracking-[0%] text-center text-[#4772F1ED]">
                Your payment is up to {formatPaidUpToDate(paymentTenantDetails?.rentPaidUpTo)}
              </span>
            </div>
          </div>
        </div>
        {/* Pay Now Button */}
        <button 
          className={`w-[150px]  h-[54px] rounded-[3px] font-poppins font-semibold text-[16px] leading-none tracking-[0%] text-center text-white mt-12 mx-auto block flex items-center justify-center ${
            isCheckoutLoading || !paymentTenantDetails?.tenantData ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          style={{ 
            background: 'linear-gradient(97.65deg, #60BBEE 0.33%, #0A72AD 93.35%)',
            boxShadow: '0px 10px 15px 0px #01739933',
            padding: '20px 40px',
          }}
          onClick={handlePayNow}
          disabled={isCheckoutLoading || !paymentTenantDetails?.tenantData}
        >
          {isCheckoutLoading ? 'Processing' : 'Pay Now'}
        </button>

        {/* Payment Confirmation Modal */}
        <ConfirmationPaymentModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          paymentDetails={{
            tenant: paymentConfirmationData?.summary?.tenantName || paymentTenantDetails?.tenantData?.tenantName || 'Tenant',
            property: paymentConfirmationData?.summary?.propertyName || paymentTenantDetails?.tenantData?.propertyName || 'Property',
            unit: paymentConfirmationData?.summary?.unitIdentifier || paymentTenantDetails?.tenantData?.unitIdentifier || 'Unit',
            landlord: paymentConfirmationData?.summary?.landlordName || paymentTenantDetails?.tenantData?.landlordName || 'Landlord',
            amount: `UGX ${formatAmount(paymentConfirmationData?.summary?.totalAmount || parseFloat(amount))}`,
            transactionId: paymentConfirmationData?.transactionId,
            paymentDate: paymentConfirmationData?.summary?.paymentDate,
            rentCoveredTo: paymentConfirmationData?.summary?.rentCoveredTo
          }}
        />

        {/* Payment Failed Modal */}
        <PaymentFailedModal
          isOpen={isFailedModalOpen}
          onClose={handleFailedModalClose}
          onConfirm={handleFailedModalRetry}
        />
      </div>
    </div>
  )
}

export default TenantRentPayment;